import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { supabase, isDemo } from '../lib/supabase'

/**
 * Who is signed in, and are they allowed in at all.
 *
 * Two separate questions, and the distinction matters. Supabase Auth answers the
 * first; a row in admin_profiles answers the second. Anyone can create an auth
 * user against a public project, so authentication alone grants nothing — RLS
 * keys every policy off is_admin(), which reads that table.
 *
 * So a user who signs in successfully but has no active profile row is shown an
 * explicit "not authorised" screen rather than an empty dashboard full of failed
 * queries, which is what they would otherwise get.
 */

const AuthContext = createContext(null)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

const DEMO_PROFILE = {
  id: 'demo-user',
  email: 'demo@coquis.co.za',
  full_name: 'Demo Operator',
  role: 'owner',
  is_active: true,
}

/**
 * Redeem an invitation, if one is waiting.
 *
 * Returns the new profile, or null when there is nothing to redeem. A missing
 * invitation is the normal case for anyone who is simply not staff, so it is not
 * treated or logged as an error.
 */
async function acceptPendingInvitation() {
  try {
    const { data, error } = await supabase.rpc('accept_invitation')
    if (error) return null
    return data ?? null
  } catch {
    return null
  }
}

/** The address an invitation link was sent to (/?invite=…), if we arrived by one. */
const readInvitedEmail = () => {
  try {
    return (new URLSearchParams(window.location.search).get('invite') ?? '').trim().toLowerCase()
  } catch {
    return ''
  }
}

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(isDemo ? DEMO_PROFILE : null)
  const [loading, setLoading] = useState(!isDemo)
  const [authError, setAuthError] = useState(null)
  const [invitedEmail, setInvitedEmail] = useState(readInvitedEmail)

  /** Drops ?invite= from the address bar and forgets it. */
  const dismissInvite = () => {
    setInvitedEmail('')
    const url = new URL(window.location.href)
    if (url.searchParams.has('invite')) {
      url.searchParams.delete('invite')
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash)
    }
  }

  useEffect(() => {
    if (isDemo) return

    let cancelled = false
    // The profile is loaded on page load and again on every auth event, and
    // those calls can overlap. Only the most recent one may set state, or a
    // slow earlier answer overwrites a newer one.
    let latest = 0

    const loadProfile = async (activeSession) => {
      const call = ++latest
      const stale = () => cancelled || call !== latest

      if (!activeSession?.user) {
        if (!stale()) {
          setProfile(null)
          setLoading(false)
        }
        return
      }

      // maybeSingle, not single: a signed-in non-admin legitimately has no row,
      // and single() would turn that expected case into a thrown error.
      const { data, error } = await supabase
        .from('admin_profiles')
        .select('*')
        .eq('id', activeSession.user.id)
        .maybeSingle()

      if (stale()) return

      if (error) {
        setAuthError(error.message)
        setProfile(null)
      } else if (!data) {
        // Signed in with no profile row. Before declaring them unauthorised,
        // check for a pending invitation — this is the invited staff member's
        // very first sign-in, and they should land in the panel rather than on
        // a "no access" screen they would have to ask about.
        //
        // accept_invitation() is safe to call unconditionally: it refuses
        // unless a pending, unexpired invitation exists for their own verified
        // e-mail address, and the caller cannot name an address or a role.
        const accepted = await acceptPendingInvitation()
        if (stale()) return
        setProfile(accepted)
        setAuthError(null)
      } else {
        setProfile(data)
        setAuthError(null)

        // Fire and forget — a dashboard must not wait on a bookkeeping write.
        if (data) {
          supabase
            .from('admin_profiles')
            .update({ last_seen_at: new Date().toISOString() })
            .eq('id', data.id)
            .then(() => {}, () => {})
        }
      }
      setLoading(false)
    }

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      setSession(data.session)
      loadProfile(data.session)
    })

    // Keeps the UI in step with a sign-out in another tab, and with token
    // refreshes that would otherwise silently expire the session.
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      loadProfile(nextSession)
    })

    return () => {
      cancelled = true
      subscription?.subscription?.unsubscribe()
    }
  }, [])

  const signIn = async (email, password) => {
    if (isDemo) throw new Error('Demo mode is already signed in.')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw new Error(error.message)
  }

  /**
   * Register a new login.
   *
   * Creating the account grants nothing on its own — access comes from the
   * invitation the owner recorded against this e-mail address, which
   * accept_invitation() redeems on the first sign-in. Someone who registers
   * without an invitation lands on the "not authorised" screen, which is the
   * intended outcome rather than a failure.
   */
  const signUp = async (email, password, fullName) => {
    if (isDemo) throw new Error('Not applicable in demo mode.')
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: fullName ? { full_name: fullName } : undefined },
    })
    if (error) throw new Error(error.message)
  }

  const signOut = async () => {
    if (isDemo) return
    await supabase.auth.signOut()
    setProfile(null)
    setSession(null)
  }

  /**
   * Claims the first owner account on an empty database.
   *
   * A fresh project has no admins and RLS forbids inserting the first one, so
   * without this the only way in is hand-editing a table in the Supabase
   * dashboard. The SQL function refuses once any admin exists, so it cannot be
   * replayed later to take over the store.
   */
  const claimOwnership = async (fullName) => {
    if (isDemo) throw new Error('Not applicable in demo mode.')
    const { data, error } = await supabase.rpc('claim_first_admin', { p_full_name: fullName ?? null })
    if (error) throw new Error(error.message)
    setProfile(data)
    return data
  }

  const sessionEmail = (session?.user?.email ?? '').toLowerCase()

  // An invitation link opened in a browser already signed in as someone else
  // must never carry on as that person — that is how an invitee ended up
  // looking at the owner's dashboard. LoginPage asks them to sign out first.
  const inviteForSomeoneElse = !isDemo && Boolean(invitedEmail && session && sessionEmail !== invitedEmail)

  // Arrived by the link and signed in as the right person: the invitation has
  // been redeemed by loadProfile, so the link has done its job.
  useEffect(() => {
    if (invitedEmail && session && profile?.is_active && sessionEmail === invitedEmail) dismissInvite()
  }, [invitedEmail, session, profile, sessionEmail])

  const value = useMemo(
    () => ({
      session,
      profile,
      loading,
      authError,
      isDemo,
      // Signed in AND allow-listed. Both halves are required.
      isAuthorised: isDemo || Boolean(session && profile?.is_active),
      isSignedIn: isDemo || Boolean(session),
      isSuspended: !isDemo && Boolean(session && profile && !profile.is_active),
      sessionEmail,
      invitedEmail,
      inviteForSomeoneElse,
      dismissInvite,
      role: profile?.role ?? null,
      // Owner or manager: catalogue, prices, settings — and, since migration
      // 18, the team (within limits the database enforces).
      canManage: isDemo || profile?.role === 'owner' || profile?.role === 'manager',
      isOwner: isDemo || profile?.role === 'owner',
      signIn,
      signUp,
      signOut,
      claimOwnership,
    }),
    [session, profile, loading, authError, invitedEmail, sessionEmail, inviteForSomeoneElse],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthContext
